import axios ,{AxiosError}from 'axios';
import { environment as envConfig } from '@/config/environment';

// Global variable to store query parameter headers for axios
let globalAxiosHeaders: Record<string, string> = {}

// Function to update global axios headers
export const setGlobalAxiosHeaders = (headers: Record<string, string>) => {
  globalAxiosHeaders = { ...headers }
}

// Configure axios globally  
// withCredentials is required to send authentication cookies like YSISessionID
axios.defaults.timeout = 30000;
axios.defaults.withCredentials = true;

// Add a global request interceptor for logging
axios.interceptors.request.use(
  (config) => {
    // Add X-Current-Path header with current URL path for HAR file analysis
    config.headers['X-Current-Path'] = window.location.pathname;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a global response interceptor for common error handling
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors here if needed
    console.error('Global axios error:', error);
    return Promise.reject(error);
  }
);

////////////////////////////  for secure api calls  ////////////////////////////


// Local storage keys for tokens (matching loginService.ts)
const TOKEN_STORAGE_KEYS = {
  ACCESS_TOKEN: 'ls.authorizationData',
  LOGIN_GUID: 'ls.LoginGUID'
};

// Function to get stored token data
const getStoredTokenData = (): { access_token: string; LoginGUID: string } | null => {
  try {
    const authData = localStorage.getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
    const loginGUID = localStorage.getItem(TOKEN_STORAGE_KEYS.LOGIN_GUID);
    
    if (authData && loginGUID) {
      // Try to parse if it's JSON, otherwise use as string
      let accessToken: string;
      try {
        const parsed = JSON.parse(authData);
        accessToken = parsed.token || parsed.access_token || authData;
      } catch {
        accessToken = authData;
      }
      
      return {
        access_token: accessToken,
        LoginGUID: loginGUID
      };
    }
  } catch (error) {
    console.warn('Failed to retrieve stored token data:', error);
  }
  return null;
};

// Create axios instance with same configuration as FloorPlans API
export const apiClientSecure = axios.create({
  baseURL: envConfig.apiPrefix.rootPath,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include Authorization header if token exists
apiClientSecure.interceptors.request.use(
  (config) => {
    // Add X-Current-Path header with current URL path for HAR file analysis
    config.headers['X-Current-Path'] = window.location.pathname;

    // Add Authorization header with JWT token
    const tokenData = getStoredTokenData();
    if (tokenData && tokenData.access_token) {
      config.headers.Authorization = `Bearer ${tokenData.access_token}`;
    }

    // Add global query parameter headers (e.g., nocache=1 -> X-nocache: 1)
    Object.keys(globalAxiosHeaders).forEach(key => {
      config.headers[key] = globalAxiosHeaders[key];
    });

    // Remove Content-Type header for FormData to let browser set it automatically
    // This is crucial for file uploads as browser needs to set the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// API client for Resportal (Resident Services) endpoints
export const apiClientSecureForResportal = axios.create({
  baseURL: envConfig.apiPrefix.rootPath.replace('/onlineleasing/', '/residentservices/'),
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include Authorization header if token exists for Resportal
apiClientSecureForResportal.interceptors.request.use(
  (config) => {
    // Add X-Current-Path header with current URL path for HAR file analysis
    config.headers['X-Current-Path'] = window.location.pathname;

    // Add Authorization header with JWT token
    const tokenData = getStoredTokenData();
    if (tokenData && tokenData.access_token) {
      config.headers.Authorization = `Bearer ${tokenData.access_token}`;
    }

    // Add global query parameter headers (e.g., nocache=1 -> X-nocache: 1)
    Object.keys(globalAxiosHeaders).forEach(key => {
      config.headers[key] = globalAxiosHeaders[key];
    });

    // Remove Content-Type header for FormData to let browser set it automatically
    // This is crucial for file uploads as browser needs to set the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Export everything needed
export { TOKEN_STORAGE_KEYS, getStoredTokenData, globalAxiosHeaders };
export { axios as default }; 